import { useEffect, useRef, useState } from "react";
import type OlMap from "ol/Map";
import type Draw from "ol/interaction/Draw";
import type { Options as DrawOptions } from "ol/interaction/Draw";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import { mouseActionButton, noModifierKeys } from "ol/events/condition";
import {
  DrawToolKind,
  finishesByDoubleClick,
  isFinishable,
  resolveRightClick,
} from "src/domains/shared/utils/annotationDrawSession";
import {
  FINISH_BY_CONDITION_MIN_POINTS,
  confirmedSketchPointCount,
} from "../utils/drawSketch";
import { onReleaseDrawTools } from "../utils/releaseDrawTools";
import { useDrawingCancelKey } from "./useDrawingCancelKey";

/** OL 기본 condition(noModifierKeys)은 버튼을 보지 않아 우클릭 pointerup 이 정점을 추가한다 — 주 버튼만 통과시킨다 */
export const primaryPointerCondition = (event: MapBrowserEvent) =>
  mouseActionButton(event) && noModifierKeys(event);

// OL removeLastPoint 는 러버밴드 끝을 새 마지막 점으로 접어 두고 다음 pointermove 까지 갱신하지 않는다 — 우클릭 좌표로 pointermove 를 다시 흘려 커서까지 바로 잇는다.
// 두 번 흘리는 이유: Draw.handlePointerMove_ 의 클릭 허용 오차 가드가 우클릭 pointerdown 뒤 첫 move 를
// (마지막 좌클릭 downPx_ 기준 거리 판정으로) 삼키고 두 번째부터 스케치를 갱신한다(ol 10.6 실측).
const REPLAY_POINTER_MOVE_COUNT = 2;
function replayPointerMove(viewport: HTMLElement, event: MouseEvent) {
  const Ctor = typeof PointerEvent === "undefined" ? MouseEvent : PointerEvent;
  for (let i = 0; i < REPLAY_POINTER_MOVE_COUNT; i += 1) {
    viewport.dispatchEvent(
      new Ctor("pointermove", {
        clientX: event.clientX,
        clientY: event.clientY,
        pointerType: "mouse",
        bubbles: true,
      } as PointerEventInit)
    );
  }
}

interface UseDrawSketchGesturesOptions {
  map: OlMap;
  isActive: boolean;
  kind: DrawToolKind;
  getDraw: () => Draw | null;
  /** 도구 해제 — 인터랙션 제거·버튼 선택 해제·커서 복원은 호출부가 한다 */
  onExit: () => void;
}

/** 선·면처럼 여러 점을 잇는 도구의 Draw 생성 옵션 — OL 의 재클릭 완료를 끄고 완료는 이 훅의 더블클릭만 한다 */
export type SketchDrawOptions = Pick<
  DrawOptions,
  "condition" | "finishCondition" | "minPoints"
>;

/**
 * 2D 그리기 손짓 어댑터 — 우클릭 되돌리기/종료, 더블클릭 완료, Esc 취소를
 * `annotationDrawSession` 규칙으로 판정해 OL Draw 에 적용한다. 재클릭 완료는 없다.
 * `pointCount` 는 활성 중 사용자가 확정한 점 개수(비활성이면 0) — 안내 칩의 단계 줄이 읽는다.
 */
export function useDrawSketchGestures({
  map,
  isActive,
  kind,
  getDraw,
  onExit,
}: UseDrawSketchGesturesOptions): {
  drawOptions: SketchDrawOptions;
  pointCount: number;
} {
  const [pointCount, setPointCount] = useState(0);
  const hasDoubleClickFinish = finishesByDoubleClick(kind);
  // 호출부가 인라인 클로저를 넘기므로 ref 로 최신을 읽는다 — 리스너 재구독(= pointCount 리셋)을 막는다
  const latest = useRef({ getDraw, onExit });
  useEffect(() => {
    latest.current = { getDraw, onExit };
  });

  const [drawOptions] = useState<SketchDrawOptions>(() =>
    hasDoubleClickFinish
      ? // OL 기본 finishCondition 은 첫/마지막 점 재클릭에 스스로 완료한다 — 끄고 더블클릭만 남긴다.
        // minPoints 는 재클릭 판정이 확정 점 수와 무관하게 항상 서게 1 — 더블클릭의 두 번째 클릭이 같은 자리에 점을 더 찍지 않는다
        {
          condition: primaryPointerCondition,
          finishCondition: () => false,
          minPoints: FINISH_BY_CONDITION_MIN_POINTS,
        }
      : { condition: primaryPointerCondition }
  );

  useDrawingCancelKey(isActive, () => {
    latest.current.getDraw()?.abortDrawing();
    latest.current.onExit();
  });

  // 지도 클릭을 따로 쓰는 기능이 시작하면 Esc 와 같이 스스로 해제한다
  useEffect(() => {
    if (!isActive) return;
    return onReleaseDrawTools(map, () => {
      latest.current.getDraw()?.abortDrawing();
      latest.current.onExit();
    });
  }, [map, isActive]);

  useEffect(() => {
    if (!isActive) return;
    const viewport = map.getViewport();

    // OL 은 pointerup 에서 점을 확정하고 그 뒤에 DOM click 이 오므로, click 시점의 스케치가 최신 확정 점이다
    const currentPointCount = () => {
      const draw = latest.current.getDraw();
      return draw ? confirmedSketchPointCount(draw, kind) : 0;
    };
    const syncPointCount = () => setPointCount(currentPointCount());

    // 더블클릭을 이루는 두 click 뒤의 확정 점 수 [직전 click, 최근 click] — 두 번째 클릭이 더한 점을 되돌리는 근거.
    // 좌표·거리로 판정하지 않는다: 지도를 축소해 진짜 꼭짓점이 촘촘해져도 건드리지 않아야 한다
    let pointCountAfterClick: [number, number] = [0, 0];
    const handleClick = () => {
      pointCountAfterClick = [pointCountAfterClick[1], currentPointCount()];
      setPointCount(pointCountAfterClick[1]);
    };

    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      const draw = latest.current.getDraw();
      const confirmed = draw ? confirmedSketchPointCount(draw, kind) : 0;
      if (resolveRightClick(confirmed) === "undo") {
        draw?.removeLastPoint();
        replayPointerMove(viewport, event);
        syncPointCount();
        return;
      }
      draw?.abortDrawing();
      latest.current.onExit();
    };

    // 더블클릭의 두 클릭 중 첫 클릭이 마지막 점을 확정하고, 같은 자리의 두 번째 클릭은 재클릭 판정(minPoints 1)에 걸려 점을 더 찍지 않는다(finishCondition off).
    // trace 를 켠 Draw 에선 첫 클릭이 기존 도형 위에서 trace 를 켜면 두 번째 클릭이 trace 판정을 받아 점을 더 찍으므로, 두 번째 click 이 더한 만큼 되돌린 뒤 센다
    const undoPointsAddedBySecondClick = (draw: Draw) => {
      const [afterFirstClick, afterSecondClick] = pointCountAfterClick;
      // 두 click 사이에 점 수가 줄었으면(음수) 늘어난 점이 없으니 되돌릴 것도 없다
      const added = Math.max(afterSecondClick - afterFirstClick, 0);
      for (let i = 0; i < added; i += 1) draw.removeLastPoint();
      if (added > 0) syncPointCount();
    };

    const handleDoubleClick = (event: MouseEvent) => {
      const draw = latest.current.getDraw();
      if (!draw) return;
      event.preventDefault();
      event.stopPropagation();
      undoPointsAddedBySecondClick(draw);
      if (!isFinishable(kind, currentPointCount())) return;
      draw.finishDrawing();
    };

    viewport.addEventListener("contextmenu", handleContextMenu);
    viewport.addEventListener("click", handleClick);
    if (hasDoubleClickFinish) {
      viewport.addEventListener("dblclick", handleDoubleClick, { capture: true });
    }
    return () => {
      viewport.removeEventListener("contextmenu", handleContextMenu);
      viewport.removeEventListener("click", handleClick);
      if (hasDoubleClickFinish) {
        viewport.removeEventListener("dblclick", handleDoubleClick, {
          capture: true,
        });
      }
      setPointCount(0);
    };
  }, [map, isActive, kind, hasDoubleClickFinish]);

  return { drawOptions, pointCount };
}
